import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Edit, Ticket, Printer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import axios from "axios";
import { format, startOfMonth, endOfMonth, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 15;

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function History() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/history?page=${page}&limit=${PAGE_SIZE}`);
      setEntries(res.data.entries);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const entryDates = useMemo(() => entries.map(e => e.date), [entries]);

  const calendarModifiers = useMemo(() => ({
    hasEntry: (date) => entryDates.includes(format(date, "yyyy-MM-dd"))
  }), [entryDates]);

  const calendarModifiersClassNames = useMemo(() => ({
    hasEntry: "bg-primary/20 text-primary font-bold"
  }), []);

  const formatCurrency = (value, asElement = false) => {
    const num = value || 0;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(Math.abs(num));
    if (num < 0) {
      return asElement
        ? <span className="text-red-500">({formatted})</span>
        : `(${formatted})`;
    }
    return asElement ? <span>{formatted}</span> : formatted;
  };

  const handleDateSelect = (date) => {
    if (date) {
      navigate(`/entry/${format(date, "yyyy-MM-dd")}`);
      setCalendarOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-8"
      data-testid="history-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-mono text-3xl font-bold tracking-tight">Entry History</h1>
          <p className="text-muted-foreground mt-1">View and edit past entries</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/print/weekly/${format(new Date(), "yyyy-MM-dd")}`)}
            className="gap-2"
            data-testid="print-weekly-btn"
          >
            <Printer className="w-4 h-4" />
            Print Weekly
          </Button>
          
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="calendar-picker-btn">
              <CalendarIcon className="w-4 h-4" />
              Jump to Date
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={selectedMonth}
              onSelect={handleDateSelect}
              modifiers={calendarModifiers}
              modifiersClassNames={calendarModifiersClassNames}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        </div>
      </div>

      {/* History Table */}
      <motion.div variants={itemVariants}>
        <Card className="border-border/50" data-testid="history-table">
          <CardHeader>
            <CardTitle className="font-mono">All Entries</CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length > 0 ? (
              <>
                {/* Top pagination */}
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-border/30">
                  <p className="text-sm text-muted-foreground font-mono" data-testid="history-page-indicator-top">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      data-testid="history-prev-btn-top"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      data-testid="history-next-btn-top"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>

                <table className="w-full table-fixed">
                    <thead>
                      <tr className="table-header">
                        <th className="p-2 text-left text-xs">Date</th>
                        <th className="p-2 text-right text-xs">Instant Sale</th>
                        <th className="p-2 text-right text-xs">Online Sale</th>
                        <th className="p-2 text-right text-xs">Total Sale</th>
                        <th className="p-2 text-right text-xs">Instant Paid Out</th>
                        <th className="p-2 text-right text-xs">Online Paid Out</th>
                        <th className="p-2 text-right text-xs">Total Paid Out</th>
                        <th className="p-2 text-right text-xs">LVM-1</th>
                        <th className="p-2 text-right text-xs">LVM-2</th>
                        <th className="p-2 text-right text-xs">Deliveries</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => {
                        const instantSale = entry.instant_sale ?? 0;
                        const onlineSale = entry.online_sale ?? 0;
                        const totalSale = entry.total_sale ?? entry.total_sales_amount ?? 0;
                        const instantPaidOut = entry.instant_paid_out ?? 0;
                        const onlinePaidOut = entry.online_paid_out ?? 0;
                        const totalPaidOut = entry.total_paid_outs ?? (instantPaidOut + onlinePaidOut);
                        return (
                          <motion.tr
                            key={entry.date}
                            variants={itemVariants}
                            className="table-row border-b border-border/30 hover:bg-muted/30 cursor-pointer"
                            onClick={() => navigate(`/entry/${entry.date}`)}
                            data-testid={`history-row-${entry.date}`}
                          >
                            <td className="table-cell">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium text-xs sm:text-sm whitespace-nowrap">
                                  {format(parseISO(entry.date), "MMM d, yyyy")}
                                </span>
                              </div>
                            </td>
                            <td className="table-cell text-right text-primary font-medium text-xs sm:text-sm">
                              {formatCurrency(instantSale, true)}
                            </td>
                            <td className="table-cell text-right text-blue-400 text-xs sm:text-sm">
                              {formatCurrency(onlineSale, true)}
                            </td>
                            <td className="table-cell text-right font-bold text-primary text-xs sm:text-sm">
                              {formatCurrency(totalSale, true)}
                            </td>
                            <td className="table-cell text-right text-orange-400 text-xs sm:text-sm">
                              {formatCurrency(instantPaidOut, true)}
                            </td>
                            <td className="table-cell text-right text-orange-400 text-xs sm:text-sm">
                              {formatCurrency(onlinePaidOut, true)}
                            </td>
                            <td className="table-cell text-right font-bold text-orange-500 text-xs sm:text-sm">
                              {formatCurrency(totalPaidOut, true)}
                            </td>
                            <td className="table-cell text-right text-purple-400 text-xs sm:text-sm">
                              {formatCurrency(entry.lvm1_value ?? 0, true)}
                            </td>
                            <td className="table-cell text-right text-purple-400 text-xs sm:text-sm">
                              {formatCurrency(entry.lvm2_value ?? 0, true)}
                            </td>
                            <td className="table-cell text-right text-green-400 text-xs sm:text-sm">
                              {formatCurrency(entry.delivery_value ?? entry.total_deliveries_amount, true)}
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/30">
                  <p className="text-sm text-muted-foreground font-mono" data-testid="history-page-indicator">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      data-testid="history-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      data-testid="history-next-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <Ticket className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-mono text-lg font-medium mb-2">No Entries Yet</h3>
                <p className="text-muted-foreground mb-6">
                  Start tracking your lottery operations by adding your first entry.
                </p>
                <Button
                  onClick={() => navigate(`/entry/${format(new Date(), "yyyy-MM-dd")}`)}
                  className="btn-primary"
                >
                  Add First Entry
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Stats */}
      {entries.length > 0 && (
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="stat-card">
            <CardContent className="pt-6">
              <p className="data-label">Total Entries</p>
              <p className="data-value">{entries.length}</p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="pt-6">
              <p className="data-label">Avg. Daily Sales</p>
              <p className="data-value text-primary">
                {formatCurrency(
                  entries.reduce((sum, e) => sum + e.total_sales_amount, 0) / entries.length
                , true)}
              </p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="pt-6">
              <p className="data-label">Total Period Sales</p>
              <p className="data-value">
                {formatCurrency(entries.reduce((sum, e) => sum + e.total_sales_amount, 0), true)}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
}
